import { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import { 
  Users, ClipboardList, BarChart3, Target, Settings, LogOut,
  Building2, TrendingUp, FileCheck, AlertCircle, Menu, X, ChevronRight, KeyRound,
  Gauge, ArrowUpRight, ArrowDownRight, Award, AlertTriangle, BookOpen
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { useAuth } from '../context/AuthContext';
import { dashboardAPI, authAPI, employeeAPI } from '../lib/api';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, RadialBarChart, RadialBar } from 'recharts';

const AdminLayout = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, isSuperAdmin } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const isSrManagement = user?.role === 'sr_management';

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  const navItems = [
    { path: '/admin', icon: BarChart3, label: 'Dashboard' },
    ...(!isSrManagement ? [{ path: '/admin/employees', icon: Users, label: 'Employee Master' }] : []),
    { path: '/admin/surveys', icon: ClipboardList, label: 'Survey Builder' },
    { path: '/admin/analytics', icon: TrendingUp, label: 'Analytics' },
    ...(!isSrManagement ? [{ path: '/admin/actions', icon: Target, label: 'Action Tracker' }] : []),
    { path: '/admin/methodology', icon: BookOpen, label: 'Methodology' },
    ...(isSuperAdmin ? [{ path: '/admin/users', icon: Settings, label: 'User Management' }] : []),
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Mobile Header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-card border-b border-border">
        <div className="flex items-center justify-between px-4 py-3">
          <div className="flex items-center gap-2">
            <img src="/gnosis-logo.png" alt="Gnosis Hospitality" className="w-32 h-auto object-contain" />
          </div>
          <Button variant="ghost" size="icon" onClick={() => setSidebarOpen(!sidebarOpen)}>
            {sidebarOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </Button>
        </div>
      </header>

      {/* Sidebar */}
      <aside className={`
        fixed top-0 left-0 h-full w-64 bg-card border-r border-border z-40
        transform transition-transform duration-300 lg:transform-none
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="p-6 border-b border-border hidden lg:block">
            <div className="flex items-center gap-3">
              <img src="/gnosis-logo.png" alt="Gnosis Hospitality" className="w-48 h-auto object-contain" />
            </div>
            <p className="text-xs text-muted-foreground mt-2">Employee Survey Portal</p>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 pt-20 lg:pt-4 space-y-1 overflow-y-auto">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
                onClick={() => setSidebarOpen(false)}
                className={`
                  flex items-center gap-3 px-4 py-3 rounded-xl transition-all
                  ${location.pathname === item.path 
                    ? 'bg-primary text-primary-foreground' 
                    : 'hover:bg-muted text-muted-foreground hover:text-foreground'
                  }
                `}
              >
                <item.icon className="h-5 w-5" />
                <span className="font-medium">{item.label}</span>
              </Link>
            ))}
          </nav>

          {/* User Section */}
          <div className="p-4 border-t border-border">
            <div className="flex items-center gap-3 mb-3">
              <div className="h-10 w-10 rounded-full bg-secondary/20 flex items-center justify-center">
                <span className="font-heading font-bold text-secondary">
                  {user?.name?.charAt(0) || 'A'}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-sm truncate">{user?.name}</p>
                <p className="text-xs text-muted-foreground truncate capitalize">
                  {user?.role?.replace('_', ' ')}
                </p>
              </div>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => { setSidebarOpen(false); navigate('/admin/change-password'); }}
                data-testid="change-password-btn"
                className="flex-1"
              >
                <KeyRound className="h-4 w-4 mr-1" />
                Password
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleLogout}
                data-testid="admin-logout-btn"
                className="flex-1"
              >
                <LogOut className="h-4 w-4 mr-1" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </aside>

      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-30 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main Content */}
      <main className="lg:ml-64 pt-16 lg:pt-0 min-h-screen flex flex-col">
        <div className="flex-1">{children}</div>
        <footer className="px-6 py-4 text-center border-t border-border bg-muted/30" data-testid="global-footer">
          <p className="text-xs text-muted-foreground">Powered by HotelPulse.in | All Rights Reserved &copy; 2026</p>
          <p className="text-xs text-muted-foreground mt-1">For support, connect at info@hotelpulse.in</p>
        </footer>
      </main>
    </div>
  );
};

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filterDept, setFilterDept] = useState('');
  const [allDepartments, setAllDepartments] = useState([]);
  const isSrManagement = user?.role === 'sr_management';

  useEffect(() => {
    loadStats(filterDept);
  }, [filterDept]);

  useEffect(() => {
    (async () => {
      try {
        const res = await employeeAPI.getDepartments();
        const depts = Array.isArray(res.data) ? res.data.filter(Boolean) : (res.data.departments || []).filter(Boolean);
        if (depts.length > 0) setAllDepartments(depts.sort());
      } catch (e) { /* ignore */ }
    })();
  }, []);

  const loadStats = async (department) => {
    try {
      const params = department ? { department } : {};
      const response = await dashboardAPI.getStats(params);
      setStats(response.data);
    } catch (error) {
      toast.error('Failed to load dashboard stats');
    } finally {
      setLoading(false);
    }
  };

  const ENGAGEMENT_COLORS = { engaged: '#22c55e', not_engaged: '#f59e0b', disengaged: '#ef4444' };

  const DEPT_COLORS = ['#1B2A41', '#E07A5F', '#81B29A', '#F2CC8F', '#3D405B', '#9B59B6', '#3498DB'];

  const getScoreColor = (score) => {
    if (score >= 4.0) return 'text-green-600';
    if (score >= 3.0) return 'text-amber-500';
    return 'text-red-500';
  };

  const getScoreBg = (score) => {
    if (score >= 4.0) return 'bg-green-50 border-green-200';
    if (score >= 3.0) return 'bg-amber-50 border-amber-200';
    return 'bg-red-50 border-red-200';
  };

  const getScoreLabel = (score) => {
    if (score >= 4.5) return 'Excellent';
    if (score >= 4.0) return 'Good';
    if (score >= 3.0) return 'Average';
    if (score >= 2.0) return 'Below Average';
    return 'Critical';
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-screen">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </AdminLayout>
    );
  }

  const eng = stats?.engagement || {};
  const hasData = eng.overall_score > 0;

  const engagementPieData = [
    { name: 'Engaged', value: eng.engaged_pct || 0, color: ENGAGEMENT_COLORS.engaged },
    { name: 'Not Engaged', value: eng.not_engaged_pct || 0, color: ENGAGEMENT_COLORS.not_engaged },
    { name: 'Actively Disengaged', value: eng.actively_disengaged_pct || 0, color: ENGAGEMENT_COLORS.disengaged },
  ].filter(d => d.value > 0);

  const gaugeData = [{ name: 'Score', value: (eng.overall_score / 5) * 100, fill: eng.overall_score >= 4 ? '#22c55e' : eng.overall_score >= 3 ? '#f59e0b' : '#ef4444' }];

  return (
    <AdminLayout>
      <div className="p-4 sm:p-6 lg:p-8 space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div>
            <h1 className="font-heading text-2xl sm:text-3xl font-bold">Engagement Dashboard</h1>
            <p className="text-sm text-muted-foreground mt-1">
              {stats?.latest_survey ? `Latest: ${stats.latest_survey.title}` : 'Employee engagement insights'}
              {filterDept && <span className="ml-2 text-primary font-medium">• Filtered: {filterDept}</span>}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Select value={filterDept || '__all__'} onValueChange={(v) => setFilterDept(v === '__all__' ? '' : v)}>
              <SelectTrigger className="w-52" data-testid="dashboard-dept-filter">
                <SelectValue placeholder="All Departments" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">All Departments</SelectItem>
                {allDepartments.map((d) => (
                  <SelectItem key={d} value={d}>{d}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button variant="outline" size="sm" onClick={() => navigate('/admin/analytics')}>
              <TrendingUp className="h-4 w-4 mr-2" />
              Detailed Analytics
            </Button>
          </div>
        </div>

        {/* Anonymity threshold notice */}
        {eng.hidden_dept_count > 0 && (
          <div className="flex items-start gap-2 text-xs text-muted-foreground bg-amber-50 border border-amber-200 rounded-lg p-3" data-testid="dashboard-threshold-notice">
            <AlertCircle className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" />
            <span>
              <strong>{eng.hidden_dept_count}</strong> department{eng.hidden_dept_count > 1 ? 's' : ''} hidden from Department Comparison — only departments with at least <strong>{eng.min_dept_threshold}</strong> responses are shown to protect individual employee anonymity.
            </span>
          </div>
        )}

        {/* KPI Strip */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="kpi-strip">
          <Card data-testid="stat-employees">
            <CardContent className="p-4 sm:p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs sm:text-sm text-muted-foreground">Employees</p>
                  <p className="font-heading text-2xl sm:text-3xl font-bold mt-1">{stats?.total_employees || 0}</p>
                </div>
                <div className="h-10 w-10 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                  <Users className="h-5 w-5 text-primary" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card data-testid="stat-surveys">
            <CardContent className="p-4 sm:p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs sm:text-sm text-muted-foreground">Active Surveys</p>
                  <p className="font-heading text-2xl sm:text-3xl font-bold mt-1">{stats?.total_surveys || 0}</p>
                </div>
                <div className="h-10 w-10 rounded-xl bg-secondary/10 flex items-center justify-center shrink-0">
                  <ClipboardList className="h-5 w-5 text-secondary" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card data-testid="stat-responses">
            <CardContent className="p-4 sm:p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs sm:text-sm text-muted-foreground">Responses</p>
                  <p className="font-heading text-2xl sm:text-3xl font-bold mt-1">{stats?.total_responses || 0}</p>
                </div>
                <div className="h-10 w-10 rounded-xl bg-green-500/10 flex items-center justify-center shrink-0">
                  <FileCheck className="h-5 w-5 text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card data-testid="stat-engagement">
            <CardContent className="p-4 sm:p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs sm:text-sm text-muted-foreground">Response Rate</p>
                  <p className="font-heading text-2xl sm:text-3xl font-bold mt-1">{eng.response_rate || 0}%</p>
                </div>
                <div className="h-10 w-10 rounded-xl bg-amber-500/10 flex items-center justify-center shrink-0">
                  <Gauge className="h-5 w-5 text-amber-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {!hasData ? (
          /* No Data State */
          <Card className="text-center py-16" data-testid="no-data-state">
            <CardContent>
              <BarChart3 className="h-16 w-16 text-muted-foreground/30 mx-auto mb-4" />
              <h3 className="font-heading text-xl font-medium mb-2">No Survey Data Yet</h3>
              <p className="text-muted-foreground mb-6 max-w-md mx-auto">
                Create a survey and collect responses to see engagement insights here.
              </p>
              {!isSrManagement && (
                <Button onClick={() => navigate('/admin/surveys')}>
                  <ClipboardList className="h-4 w-4 mr-2" />
                  Create Survey
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Row 1: Engagement Score + Engagement Categories + Response Rate */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Overall Engagement Score */}
              <Card className={`border ${getScoreBg(eng.overall_score)}`} data-testid="engagement-score">
                <CardContent className="p-6 text-center">
                  <p className="text-sm font-medium text-muted-foreground mb-2">Overall Engagement Score</p>
                  <div className="relative mx-auto" style={{width: 160, height: 100}}>
                    <ResponsiveContainer width="100%" height={100}>
                      <RadialBarChart
                        cx="50%" cy="100%" innerRadius="70%" outerRadius="100%"
                        startAngle={180} endAngle={0} barSize={14}
                        data={gaugeData}
                      >
                        <RadialBar background dataKey="value" cornerRadius={10} />
                      </RadialBarChart>
                    </ResponsiveContainer>
                  </div>
                  <p className={`font-heading text-4xl font-bold mt-1 ${getScoreColor(eng.overall_score)}`}>
                    {eng.overall_score}
                  </p>
                  <p className="text-xs text-muted-foreground">out of 5.0</p>
                  <Badge variant="outline" className={`mt-2 ${getScoreColor(eng.overall_score)}`}>
                    {getScoreLabel(eng.overall_score)}
                  </Badge>
                </CardContent>
              </Card>

              {/* Engagement Categories */}
              <Card data-testid="engagement-categories">
                <CardHeader className="pb-2">
                  <CardTitle className="font-heading text-base">Engagement Breakdown</CardTitle>
                </CardHeader>
                <CardContent className="px-6 pb-6">
                  <div className="flex items-center justify-center mb-4" style={{height: 130}}>
                    {engagementPieData.length > 0 ? (
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie data={engagementPieData} cx="50%" cy="50%" innerRadius={35} outerRadius={55} dataKey="value" stroke="none">
                            {engagementPieData.map((entry, i) => (
                              <Cell key={i} fill={entry.color} />
                            ))}
                          </Pie>
                          <Tooltip formatter={(val) => `${val}%`} />
                        </PieChart>
                      </ResponsiveContainer>
                    ) : null}
                  </div>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full bg-green-500" />
                        <span className="text-sm">Engaged</span>
                      </div>
                      <span className="font-heading font-bold text-green-600">{eng.engaged_pct}%</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full bg-amber-500" />
                        <span className="text-sm">Not Engaged</span>
                      </div>
                      <span className="font-heading font-bold text-amber-500">{eng.not_engaged_pct}%</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full bg-red-500" />
                        <span className="text-sm">Actively Disengaged</span>
                      </div>
                      <span className="font-heading font-bold text-red-500">{eng.actively_disengaged_pct}%</span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Section-wise Scores */}
              <Card data-testid="section-scores">
                <CardHeader className="pb-2">
                  <CardTitle className="font-heading text-base">Section Scores</CardTitle>
                </CardHeader>
                <CardContent className="px-6 pb-6">
                  {(eng.section_scores || []).length > 0 ? (
                    <div className="space-y-3">
                      {eng.section_scores.map((sec, i) => (
                        <div key={i}>
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-sm truncate mr-2">{sec.section}</span>
                            <span className={`text-sm font-bold ${getScoreColor(sec.score)}`}>{sec.score}</span>
                          </div>
                          <Progress value={(sec.score / 5) * 100} className="h-2" />
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground text-center py-8">No section data</p>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Row 2: Department Comparison */}
            <Card data-testid="department-comparison">
              <CardHeader className="pb-2">
                <CardTitle className="font-heading text-base">Department Comparison</CardTitle>
              </CardHeader>
              <CardContent>
                {(eng.department_scores || []).length > 0 ? (
                  <div style={{height: 280}}>
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={eng.department_scores} margin={{top: 5, right: 20, left: 0, bottom: 40}}>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} />
                        <XAxis dataKey="department" tick={{fontSize: 12}} angle={-25} textAnchor="end" />
                        <YAxis domain={[0, 5]} tick={{fontSize: 12}} />
                        <Tooltip formatter={(val) => [`${val} / 5.0`, 'Score']} />
                        <Bar dataKey="score" radius={[6, 6, 0, 0]} maxBarSize={50}>
                          {(eng.department_scores || []).map((entry, i) => (
                            <Cell key={i} fill={entry.score >= 4 ? '#22c55e' : entry.score >= 3 ? '#f59e0b' : '#ef4444'} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground text-center py-8">No department data</p>
                )}
              </CardContent>
            </Card>

            {/* Row 3: Strengths + Improvement Areas */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card data-testid="top-strengths">
                <CardHeader className="pb-2">
                  <div className="flex items-center gap-2">
                    <Award className="h-5 w-5 text-green-600" />
                    <CardTitle className="font-heading text-base">Top Strengths</CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  {(eng.top_strengths || []).length > 0 ? (
                    <div className="space-y-3">
                      {eng.top_strengths.map((q, i) => (
                        <div key={i} className="flex items-start gap-3 p-3 rounded-lg bg-green-50/50">
                          <span className="text-xs font-bold text-green-600 bg-green-100 rounded-full h-6 w-6 flex items-center justify-center shrink-0 mt-0.5">
                            {i + 1}
                          </span>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm leading-snug">{q.question}</p>
                            <div className="flex items-center gap-2 mt-1">
                              <Badge variant="outline" className="text-[10px] text-green-600">{q.section}</Badge>
                              <span className="text-xs text-muted-foreground">{q.responses} responses</span>
                            </div>
                          </div>
                          <span className="font-heading font-bold text-green-600 shrink-0">{q.score}</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground text-center py-8">No data</p>
                  )}
                </CardContent>
              </Card>

              <Card data-testid="areas-improvement">
                <CardHeader className="pb-2">
                  <div className="flex items-center gap-2">
                    <AlertTriangle className="h-5 w-5 text-amber-500" />
                    <CardTitle className="font-heading text-base">Areas for Improvement</CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  {(eng.areas_improvement || []).length > 0 ? (
                    <div className="space-y-3">
                      {eng.areas_improvement.map((q, i) => (
                        <div key={i} className="flex items-start gap-3 p-3 rounded-lg bg-red-50/50">
                          <span className="text-xs font-bold text-red-500 bg-red-100 rounded-full h-6 w-6 flex items-center justify-center shrink-0 mt-0.5">
                            {i + 1}
                          </span>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm leading-snug">{q.question}</p>
                            <div className="flex items-center gap-2 mt-1">
                              <Badge variant="outline" className="text-[10px] text-red-500">{q.section}</Badge>
                              <span className="text-xs text-muted-foreground">{q.responses} responses</span>
                            </div>
                          </div>
                          <span className="font-heading font-bold text-red-500 shrink-0">{q.score}</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground text-center py-8">No data</p>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Row 4: Score Distribution */}
            <Card data-testid="score-distribution">
              <CardHeader className="pb-2">
                <CardTitle className="font-heading text-base">Score Distribution</CardTitle>
              </CardHeader>
              <CardContent>
                <div style={{height: 220}}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={eng.score_distribution || []} margin={{top: 5, right: 20, left: 0, bottom: 5}}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} />
                      <XAxis dataKey="label" tick={{fontSize: 12}} />
                      <YAxis tick={{fontSize: 12}} allowDecimals={false} />
                      <Tooltip />
                      <Bar dataKey="count" name="Responses" radius={[6, 6, 0, 0]} maxBarSize={60}>
                        {(eng.score_distribution || []).map((entry, i) => (
                          <Cell key={i} fill={['#ef4444', '#f97316', '#f59e0b', '#22c55e', '#16a34a'][i]} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </>
        )}

        {/* Quick Actions */}
        <Card data-testid="quick-actions">
          <CardHeader className="pb-2">
            <CardTitle className="font-heading text-base">Quick Actions</CardTitle>
          </CardHeader>
          <CardContent>
            <div className={`grid grid-cols-1 sm:grid-cols-2 ${isSrManagement ? '' : 'lg:grid-cols-4'} gap-3`}>
              {!isSrManagement && (
                <Button 
                  variant="outline" 
                  className="h-auto p-4 justify-start overflow-hidden"
                  onClick={() => navigate('/admin/employees')}
                >
                  <Users className="h-5 w-5 shrink-0 mr-3 text-primary" />
                  <div className="text-left min-w-0">
                    <p className="font-medium truncate">Manage Employees</p>
                    <p className="text-xs text-muted-foreground truncate">Add or update employee data</p>
                  </div>
                </Button>
              )}
              <Button 
                variant="outline" 
                className="h-auto p-4 justify-start overflow-hidden"
                onClick={() => navigate('/admin/surveys')}
              >
                <ClipboardList className="h-5 w-5 shrink-0 mr-3 text-secondary" />
                <div className="text-left min-w-0">
                  <p className="font-medium truncate">{isSrManagement ? 'View Surveys' : 'Create Survey'}</p>
                  <p className="text-xs text-muted-foreground truncate">{isSrManagement ? 'Review survey results' : 'Build a new survey'}</p>
                </div>
              </Button>
              <Button 
                variant="outline" 
                className="h-auto p-4 justify-start overflow-hidden"
                onClick={() => navigate('/admin/analytics')}
              >
                <TrendingUp className="h-5 w-5 shrink-0 mr-3 text-green-600" />
                <div className="text-left min-w-0">
                  <p className="font-medium truncate">View Analytics</p>
                  <p className="text-xs text-muted-foreground truncate">Engagement insights</p>
                </div>
              </Button>
              {/* HR User Guide removed */}
            </div>
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
};

export { AdminLayout };
export default AdminDashboard;
